import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

// Security / Banned IPs — admin view into the host fail2ban daemon.
// Bans are GLOBAL: a banned IP is blocked on SSH and every dialer port
// (all jails share one iptables chain). Policy: 10 failed attempts in
// 10 minutes → 1 hour ban, enforced automatically by fail2ban.
export default function Security() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newIp, setNewIp] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminAPI.securityBans();
      setData(d);
    } catch (err) {
      setError(err.error || 'Failed to reach server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showNotice = msg => { setNotice(msg); setTimeout(() => setNotice(null), 4000); };

  const handleBan = async e => {
    e.preventDefault();
    const ip = newIp.trim();
    if (!ip) return;
    setBusy(true);
    try {
      await adminAPI.banIp(ip);
      setNewIp('');
      showNotice(`${ip} banned globally (1 hour, or until unbanned)`);
      load();
    } catch (err) {
      alert(err.error || 'Ban failed');
    } finally { setBusy(false); }
  };

  const handleUnban = async ip => {
    if (!window.confirm(`Unban ${ip}? It will regain access to everything (SSH, UIs, SIP).`)) return;
    setBusy(true);
    try {
      await adminAPI.unbanIp(ip);
      showNotice(`${ip} unbanned`);
      load();
    } catch (err) {
      alert(err.error || 'Unban failed');
    } finally { setBusy(false); }
  };

  const offline = data?.fail2ban === 'offline';
  const banned = data?.banned || [];
  const jails = data?.jails || [];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Security</h1>
          <p className="page-subtitle">
            Global bans · 10 failed attempts in 10 min → blocked everywhere for 1 hour
          </p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {notice && <div className="alert alert-success" style={{ marginBottom: 16 }}>{notice}</div>}

      {/* fail2ban daemon status */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
            background: offline ? 'var(--danger)' : 'var(--success)',
            boxShadow: `0 0 8px ${offline ? 'var(--danger)' : 'var(--success)'}`
          }} />
          <strong>fail2ban {offline ? 'offline' : 'active'}</strong>
          {!offline && (
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              · jails: {jails.map(j => (
                <span key={j} style={{
                  display: 'inline-block', margin: '2px 4px 2px 0', padding: '2px 8px',
                  borderRadius: 10, fontSize: 11,
                  background: 'var(--bg-secondary, rgba(255,255,255,0.06))',
                  border: '1px solid var(--border, rgba(255,255,255,0.1))'
                }}>{j}</span>
              ))}
            </span>
          )}
        </div>
        {offline && (
          <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            The ban daemon is not reachable from the backend container. Automatic bans are NOT
            active — check that fail2ban is running on the host and its socket is mounted.
          </p>
        )}
      </div>

      {/* Manual ban form */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 16px' }}>
        <form onSubmit={handleBan} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: 260 }}>
            <div className="form-label" style={{ marginBottom: 4 }}>Manually ban an IP</div>
            <input className="input" value={newIp} onChange={e => setNewIp(e.target.value)}
              placeholder="e.g. 203.0.113.45" disabled={busy || offline} required />
          </div>
          <button type="submit" className="btn btn-danger" disabled={busy || offline || !newIp.trim()}>
            {busy ? 'Working...' : '🚫 Ban globally'}
          </button>
        </form>
      </div>

      {/* Banned IPs table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>{error}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Banned IP</th>
                <th>Banned by jail(s)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {banned.map(b => (
                <tr key={b.ip}>
                  <td className="mono" style={{ fontSize: 14, color: 'var(--danger)' }}>{b.ip}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{(b.jails || []).join(', ')}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" disabled={busy}
                      onClick={() => handleUnban(b.ip)}>Unban</button>
                  </td>
                </tr>
              ))}
              {banned.length === 0 && (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  {offline ? 'Bans unavailable — fail2ban offline' : 'No IPs are currently banned 🎉'}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
